// Live preview iframe — renders `renderEmail(email)` into an `<iframe
// srcdoc>`, debounced 50ms via `useDebounced`. Per spec/build-plan.md
// P-029 row + spec/tech-arch.md §3.3 + §8.
//
// **Why iframe srcdoc (not inline DOM render).** Email HTML is a full
// HTML5 document — own `<html>`/`<head>`/`<body>`, embedded media-query
// `<style>` (mobile breakpoint + dark-mode per D-019/D-081), inline
// element styles per design-system.md §Email HTML conventions. Inline
// rendering would either pollute the parent app's CSS scope OR require
// extracting just the body fragment (losing the head's media queries).
// `<iframe srcdoc>` isolates the rendered email in its own browsing
// context — own CSS scope, own document object, own scroll — matching
// the chapter staff editor's preview-vs-form divider precisely.
//
// **srcdoc swaps don't reload the iframe document.** Per WHATWG HTML
// spec, setting the `srcdoc` attribute re-renders the iframe contents
// but the `iframe.contentWindow` reference stays stable (no `load`
// event, no parent-document remount). The 50ms debounce frequency keeps
// scroll-position resets imperceptible at V1.
//
// **Sandbox = `allow-same-origin`** (defense-in-depth). Without
// `allow-scripts` (omitted), scripts in the iframe are blocked even if
// they sneak past DOMPurify (D-022) at the email-output layer. The
// rendered email has no scripts (sanitizer strips them at richText
// surfaces; document-shell.ts inserts none); the sandbox is the third
// layer behind kernel-escape (D-021) + sanitize (D-022). `allow-same-
// origin` keeps inline-style URL resolution working without surprise
// (matches the standard HTML-preview iframe idiom).
//
// **Error recovery.** On `renderEmail` throw, keep the last-good iframe
// mounted with an overlay banner + Retry button. Retry bumps a tick
// state in the memo deps so a fresh attempt fires even when `email` is
// reference-identical (transient invariants, dev-mode hot-reload). If
// no last-good render exists yet, the overlay fills the area instead.
//
// **PRD §6 100ms preview-update budget.** 50ms debounce + sub-ms
// `renderEmail()` walk (15 blocks × ~2KB body per Tech Arch §8) +
// browser-internal srcdoc swap (typically <10ms for documents under
// 100KB) = total visible-update latency comfortably under 100ms.
// FieldRenderer memoization (D-087 / D-089 / D-090 deferred) revisits
// only if the budget is breached when editor screens at P-031/P-032
// integrate the form with the preview.
//
// **Viewport toggle (P-030; mobile rewritten at P-037.6).** Optional prop:
//
// - `viewport?: Viewport` (default `'desktop'`) — controls the wrapper
//   width + iframe transform.
//
//   - **Desktop:** wrapper is 680 px, iframe is 680 px, no transform. Email
//     renders at its natural width (matching `tokens.layout.containerWidth`).
//
//   - **Mobile:** wrapper is 375 px, iframe is **still 680 px** (so the
//     email renders at its natural design width), and the iframe is CSS-
//     scaled via `transform: scale(375/680)` with `transform-origin: top
//     left`. Layout height compensated (`calc(100% * 680 / 375)` ≈ 181 %)
//     so the post-scale visual height equals the wrapper height. The
//     wrapper's `overflow: hidden` clips the iframe's layout box to its
//     visual area.
//
//     **Why scale instead of just narrowing the iframe.** Real mobile
//     email clients (Gmail iOS, Gmail Android, Outlook iOS) auto-shrink
//     rigid-width emails proportionally to fit the device viewport —
//     users see a smaller, complete email and pinch-to-zoom for detail.
//     Pure narrowing (the P-030 → P-037.5 implementation) clipped 305 px
//     off the right of every Block, which is not how any mainstream
//     client renders. Apple Mail iOS sometimes renders at natural width
//     with pan-and-zoom, but the scale-to-fit simulation matches the
//     dominant case.
//
//     The document-shell.ts mobile-breakpoint media query (`@media
//     (max-width: 480px) { td[width] { width: 100% !important; display:
//     block !important; } }`) still fires inside the scaled iframe —
//     the iframe's `<meta viewport>` + 375 px wrapper combine with the
//     embedded `@media` rule to collapse multi-column grid Blocks
//     (chapter-whatsapp 3-col, member-list 3-col, etc.) to
//     single-column, matching real mobile rendering of those Blocks.
//
//   Constant style references avoid per-render `{ width: ... }` /
//   `{ transform: ... }` allocations on the hot path. `w-full` is
//   dropped from the wrapper base class — width is always viewport-
//   driven.
//
// **Color-scheme toggle removed (D-100, P-037 close).** The shipped
// email-output is light-only; the dark-mode shell + the in-app preview
// Dark toggle were dropped together. Email clients with native dark-
// mode behaviors (Apple Mail auto-invert; Gmail's auto-color-transform)
// apply their own rendering on top of the light authored output.

import { useEffect, useMemo, useRef, useState } from 'preact/hooks'

import { errorMessage } from '../../lib/error'
import { mergeClass } from '../../lib/merge-class'
import { renderEmail } from '../../render/render-email'
import type { Email } from '../../store/schemas'
import type { Viewport } from '../../types'
import { useDebounced } from '../hooks/use-debounced'

const DEBOUNCE_MS = 50

// Module-level style constants — selected by `viewport` instead of
// allocating fresh objects per render. PreviewIframe is the hot path
// (parent re-renders per keystroke); even with `useMemo` caching the
// rendered HTML, the iframe JSX itself still re-renders, and a fresh
// inline-style object reference adds Preact prop-diff churn for no gain.
const NATURAL_WIDTH = 680 // matches tokens.layout.containerWidth
const MOBILE_VIEWPORT_WIDTH = 375 // standard mobile viewport simulation
const MOBILE_SCALE = MOBILE_VIEWPORT_WIDTH / NATURAL_WIDTH

const STYLE_WRAPPER_DESKTOP = { width: NATURAL_WIDTH } as const
const STYLE_WRAPPER_MOBILE = { width: MOBILE_VIEWPORT_WIDTH } as const
const STYLE_IFRAME_DESKTOP = { width: NATURAL_WIDTH } as const
// Mobile iframe stays at the natural 680 px width so the email renders
// as authored; CSS `transform: scale` shrinks the rendered pixels to
// the 375 px wrapper. Layout `height` compensated so the post-scale
// visual height matches the wrapper height. See module-header comment.
const STYLE_IFRAME_MOBILE = {
  width: NATURAL_WIDTH,
  height: `calc(100% * ${NATURAL_WIDTH} / ${MOBILE_VIEWPORT_WIDTH})`,
  transform: `scale(${MOBILE_SCALE})`,
  transformOrigin: 'top left',
} as const

const WRAPPER_BASE_CLASS = 'block h-full overflow-hidden bg-white'
const WRAPPER_RELATIVE_CLASS = 'relative h-full overflow-hidden bg-white'
const IFRAME_BASE_CLASS = 'block h-full border-0 bg-white'

const ERROR_FILL_CLASS =
  'flex h-full w-full items-center justify-center gap-3 border border-red-300 ' +
  'bg-red-50 p-4 text-sm text-red-900'

const ERROR_OVERLAY_CLASS =
  'absolute inset-x-0 top-0 z-10 flex items-center justify-between gap-3 ' +
  'border-b border-red-300 bg-red-50 px-3 py-2 text-sm text-red-900'

const RETRY_BUTTON_CLASS =
  'shrink-0 rounded border border-red-300 bg-white px-2.5 py-1 text-xs ' +
  'font-medium text-red-900 hover:bg-red-100 focus:outline-none focus:ring-2 ' +
  'focus:ring-red-500 focus:ring-offset-1'

interface PreviewIframeProps {
  readonly email: Email
  readonly className?: string
  readonly viewport?: Viewport
  /**
   * Forwarded to `renderEmail` as `opts.isolated`. When `true`, the
   * single-block envelope used by the Block Gallery doesn't trigger
   * `section-heading`'s `followingBlocksAllHidden` auto-hide vacuously.
   * Production callers (EmailEditorScreen, etc.) MUST NOT set this —
   * the auto-hide is correct behavior in real emails. Default: `false`.
   */
  readonly isolated?: boolean
  /**
   * P-037.15 (D-115). When `true`, the iframe wrapper auto-grows to the
   * rendered content height (measured via `body.scrollHeight` after each
   * srcdoc swap). Used by the Block Gallery so tall blocks (chapter-
   * whatsapp, member-list) aren't clipped at the wrapper's
   * `min-h-[600px]`. Production callers (EmailEditorScreen) leave this
   * unset — their preview pane keeps its scrollable fixed-height behavior.
   * Default: `false`.
   */
  readonly autoHeight?: boolean
}

type RenderResult =
  | { readonly ok: true; readonly html: string }
  | { readonly ok: false; readonly error: string }

export function PreviewIframe({
  email,
  className,
  viewport = 'desktop',
  isolated = false,
  autoHeight = false,
}: PreviewIframeProps) {
  const debouncedEmail = useDebounced(email, DEBOUNCE_MS)
  const [retryTick, setRetryTick] = useState(0)
  const [contentHeight, setContentHeight] = useState<number | undefined>(undefined)
  const lastGoodHtmlRef = useRef<string>('')
  const iframeRef = useRef<HTMLIFrameElement>(null)

  const result: RenderResult = useMemo(() => {
    try {
      return { ok: true, html: renderEmail(debouncedEmail, { isolated }) }
    } catch (e) {
      return { ok: false, error: errorMessage(e) }
    }
  }, [debouncedEmail, retryTick, isolated])

  useEffect(() => {
    if (result.ok) lastGoodHtmlRef.current = result.html
  }, [result])

  // Auto-height: re-measure iframe content after each render. srcdoc
  // swaps don't fire `load`, so we run on `result` / `viewport` changes
  // with a small setTimeout to let the new srcdoc paint. `load` listener
  // is the belt — `setTimeout(50)` is the suspenders for steady-state
  // updates after the first mount.
  useEffect(() => {
    if (!autoHeight || !result.ok) return
    const iframe = iframeRef.current
    if (iframe === null) return
    let cancelled = false
    const measure = () => {
      if (cancelled) return
      const doc = iframe.contentDocument
      if (doc === null || doc.body === null) return
      const h = doc.body.scrollHeight
      if (h > 0) setContentHeight(h)
    }
    const onLoad = (): void => {
      measure()
    }
    iframe.addEventListener('load', onLoad)
    const t = setTimeout(measure, 50)
    return () => {
      cancelled = true
      iframe.removeEventListener('load', onLoad)
      clearTimeout(t)
    }
  }, [autoHeight, result, viewport])

  const isMobile = viewport === 'mobile'
  // Wrapper visual height = contentHeight (desktop, 1:1) or contentHeight *
  // MOBILE_SCALE (mobile — iframe is scaled down by `transform: scale`,
  // so the wrapper's visual height shrinks proportionally to keep the
  // post-scale rendering flush).
  const dynamicWrapperHeight =
    autoHeight && contentHeight !== undefined
      ? isMobile
        ? contentHeight * MOBILE_SCALE
        : contentHeight
      : undefined
  const wrapperBaseStyle = isMobile ? STYLE_WRAPPER_MOBILE : STYLE_WRAPPER_DESKTOP
  const wrapperStyle =
    dynamicWrapperHeight !== undefined
      ? { ...wrapperBaseStyle, height: dynamicWrapperHeight }
      : wrapperBaseStyle
  const iframeStyle = isMobile ? STYLE_IFRAME_MOBILE : STYLE_IFRAME_DESKTOP

  if (!result.ok) {
    const lastGood = lastGoodHtmlRef.current
    if (lastGood === '') {
      return (
        <div role="alert" class={mergeClass(ERROR_FILL_CLASS, className)}>
          <span>Preview render failed: {result.error}</span>
          <button
            type="button"
            class={RETRY_BUTTON_CLASS}
            onClick={() => setRetryTick((n) => n + 1)}
          >
            Retry
          </button>
        </div>
      )
    }
    return (
      <div class={mergeClass(WRAPPER_RELATIVE_CLASS, className)} style={wrapperStyle}>
        <iframe
          ref={iframeRef}
          title="Email preview (last successful render)"
          srcdoc={lastGood}
          sandbox="allow-same-origin"
          class={IFRAME_BASE_CLASS}
          style={iframeStyle}
        />
        <div role="alert" class={ERROR_OVERLAY_CLASS}>
          <span>Preview render failed: {result.error}</span>
          <button
            type="button"
            class={RETRY_BUTTON_CLASS}
            onClick={() => setRetryTick((n) => n + 1)}
          >
            Retry
          </button>
        </div>
      </div>
    )
  }

  return (
    <div class={mergeClass(WRAPPER_BASE_CLASS, className)} style={wrapperStyle}>
      <iframe
        ref={iframeRef}
        title="Email preview"
        srcdoc={result.html}
        sandbox="allow-same-origin"
        class={IFRAME_BASE_CLASS}
        style={iframeStyle}
      />
    </div>
  )
}
